import { useCallback } from 'react';
import { useFormContext, useWatch } from '@common-fe/common-fe';
import groupBy from 'lodash/groupBy';

import { CategoryTypeDto } from '@/modules/ProcessingDefinition/Services/Services.types';

import {
  PayoutDefinitionField,
  PayoutDefinitionFormValues,
  PayoutOrderCategory,
  PayoutOrderService,
} from '../../../PayoutDefinitionSetup.types';
import { SelectedService } from '../widgets/AddServicesModal';

import { buildServiceRowId } from './payoutOrderRowId.utils';
import { getServiceColumnErrorKey } from './usePayoutOrderValidation.utils';

const toPayoutOrderService = (service: SelectedService): PayoutOrderService => ({
  rowId: buildServiceRowId(service.id),
  id: service.id,
  name: service.name,
  serviceType: service.serviceType,
  plans: [],
});

const usePayoutOrderActions = () => {
  const { setValue, getValues, clearErrors } = useFormContext<PayoutDefinitionFormValues>();

  const categories = useWatch<PayoutDefinitionFormValues>({
    name: PayoutDefinitionField.categories,
  }) as PayoutOrderCategory[];

  /**
   * Merges selected services into form categories.
   * Deduplicates by service id — the modal shows all services including already added ones,
   * so duplicates are filtered here on submit.
   */
  const handleAddServices = useCallback(
    (selectedServices: SelectedService[]) => {
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];
      const servicesByCategory = groupBy(selectedServices, (service) => service.categoryType);

      const updatedCategories = [...currentCategories];

      Object.entries(servicesByCategory).forEach(([categoryType, services]) => {
        const existingIndex = updatedCategories.findIndex((c) => c.categoryType === categoryType);

        if (existingIndex >= 0) {
          // Merge into existing category, skip already added services
          const existing = updatedCategories[existingIndex];
          const existingIds = new Set(existing.services.map((service) => service.id));
          const newServices = services
            .filter((service) => !existingIds.has(service.id))
            .map(toPayoutOrderService);

          updatedCategories[existingIndex] = {
            ...existing,
            services: [...existing.services, ...newServices],
          };
        } else {
          // Create new category
          updatedCategories.push({
            categoryType: categoryType as CategoryTypeDto,
            services: services.map(toPayoutOrderService),
          });
        }
      });

      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });
    },
    [getValues, setValue]
  );

  /** Removes entire category with all its services */
  const handleRemoveCategory = useCallback(
    (categoryType: string) => {
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];
      const columnsCount = getValues(PayoutDefinitionField.columnsCount) || 0;

      // Clear all per-service errors for the removed category
      const removedCategory = currentCategories.find((c) => c.categoryType === categoryType);
      if (removedCategory) {
        const errorKeys = removedCategory.services.flatMap((service) =>
          Array.from({ length: columnsCount }, (_, col) => getServiceColumnErrorKey(service.rowId, col + 1))
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (errorKeys.length > 0) clearErrors(errorKeys as any);
      }

      const updatedCategories = currentCategories.filter((c) => c.categoryType !== categoryType);
      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });
    },
    [getValues, setValue, clearErrors]
  );

  /** Removes a single service (matched by unique rowId); auto-removes the category if it becomes empty */
  const handleRemoveService = useCallback(
    (categoryType: string, rowId: string) => {
      const currentCategories = getValues(PayoutDefinitionField.categories) || [];
      const columnsCount = getValues(PayoutDefinitionField.columnsCount) || 0;

      // Clear per-service errors for the removed service
      const errorKeys = Array.from({ length: columnsCount }, (_, col) =>
        getServiceColumnErrorKey(rowId, col + 1)
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (errorKeys.length > 0) clearErrors(errorKeys as any);

      const updatedCategories = currentCategories
        .map((category) => {
          if (category.categoryType !== categoryType) return category;
          return {
            ...category,
            services: category.services.filter((service) => service.rowId !== rowId),
          };
        })
        .filter((category) => category.services.length > 0);

      setValue(PayoutDefinitionField.categories, updatedCategories, { shouldDirty: true });
    },
    [getValues, setValue, clearErrors]
  );

  return {
    categories: categories || [],
    handleAddServices,
    handleRemoveCategory,
    handleRemoveService,
  };
};

export default usePayoutOrderActions;
